"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import DataTable from "../DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import type { JournalRow } from "./JournalTable";
import DocumentPreviewButton from "./DocumentPreviewButton";

type BatchView = {
  id: string;
  batchId: string;
  batchDate: string;
  createdAt: string;
  status: "OPEN" | "REVIEWED" | "FINAL";
  totals: {
    count: number;
    gross: number;
  };
  rows: JournalRow[];
};

function parseMoney(v: string): number {
  const s = (v || "")
    .toString()
    .replace(/[^0-9,.\-]/g, "")
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

type LockConflict = {
  lockedBy: string;
  lockedByName?: string | null;
  expiresAt: string;
};

function formatRemaining(lockUntil: string | null | undefined, nowMs: number): string {
  if (!lockUntil) return "";
  const target = new Date(lockUntil).getTime();
  if (!Number.isFinite(target)) return "";
  const diff = Math.max(0, target - nowMs);
  const totalSec = Math.floor(diff / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function renderStatus(row: JournalRow) {
  const SAFE_CONFIDENCE_THRESHOLD = 0.9;
  if (row.status === "FINAL") return <span className="pill pill-final">FINAL</span>;
  if (
    typeof row.aiConfidence === "number" &&
    Number.isFinite(row.aiConfidence) &&
    row.aiConfidence >= SAFE_CONFIDENCE_THRESHOLD
  ) {
    return <span className="pill pill-review">{(row.aiConfidence * 100).toFixed(1)} % sicher</span>;
  }
  return <span className="pill pill-provisional">PROVISORISCH</span>;
}

function LockCell({
  row,
  reviewerId,
  reviewerName,
  onError,
  onRefresh
}: {
  row: JournalRow;
  reviewerId: string;
  reviewerName: string;
  onError: (m: string | null) => void;
  onRefresh: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  if (!row.id || row.status === "FINAL") return <span style={{ color: "var(--muted)" }}>-</span>;

  const mine = !!row.lockBy && row.lockBy === reviewerId;
  const lockedByOther = !!row.lockBy && row.lockBy !== reviewerId;
  const lockLabel = row.lockBy
    ? `${mine ? "Von dir" : `Von ${row.lockBy}`}${row.lockUntil ? ` · ${formatRemaining(row.lockUntil, nowMs)}` : ""}`
    : "Frei";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span className={row.lockBy ? (mine ? "pill pill-review" : "pill pill-provisional") : "pill"}>{lockLabel}</span>
      {mine ? (
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy}
          onClick={async () => {
            if (!reviewerId) {
              onError("Bitte zuerst deine Reviewer-ID setzen.");
              return;
            }
            setBusy(true);
            onError(null);
            try {
              const res = await fetch("/api/review-locks/release", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ entryId: row.id, reviewerId })
              });
              const payload = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(payload?.error || "Sperre konnte nicht gelöst werden");
              onRefresh();
            } catch (err) {
              onError(err instanceof Error ? err.message : "Sperre konnte nicht gelöst werden");
            } finally {
              setBusy(false);
            }
          }}
        >
          Freigeben
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy || lockedByOther}
          onClick={async () => {
            if (!reviewerId) {
              onError("Bitte zuerst deine Reviewer-ID setzen.");
              return;
            }
            setBusy(true);
            onError(null);
            try {
              const res = await fetch("/api/review-locks/claim", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  entryId: row.id,
                  reviewerId,
                  reviewerName,
                  ttlMinutes: 20
                })
              });
              if (res.status === 409) {
                const payload = (await res.json().catch(() => ({}))) as { conflict?: LockConflict };
                const c = payload.conflict;
                const who = c?.lockedByName || c?.lockedBy || "anderer Benutzer";
                throw new Error(`Zeile ist bereits gesperrt durch ${who}`);
              }
              const payload = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(payload?.error || "Sperre konnte nicht gesetzt werden");
              onRefresh();
            } catch (err) {
              onError(err instanceof Error ? err.message : "Sperre konnte nicht gesetzt werden");
            } finally {
              setBusy(false);
            }
          }}
        >
          Sperren
        </button>
      )}
    </div>
  );
}

export default function BatchScreen({
  provisional,
  batches,
}: {
  provisional: JournalRow[];
  batches: BatchView[];
}) {
  const router = useRouter();
  const [creating, setCreating] = React.useState(false);
  const [actionBusy, setActionBusy] = React.useState<"review" | "finalize" | null>(null);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [reviewerId, setReviewerId] = React.useState("mitarbeiter-1");
  const [reviewerName, setReviewerName] = React.useState("Mitarbeiter");

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const savedId = window.localStorage.getItem("osf.reviewerId");
    const savedName = window.localStorage.getItem("osf.reviewerName");
    if (savedId) setReviewerId(savedId);
    if (savedName) setReviewerName(savedName);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("osf.reviewerId", reviewerId);
    window.localStorage.setItem("osf.reviewerName", reviewerName);
  }, [reviewerId, reviewerName]);
  React.useEffect(() => {
    const t = window.setInterval(() => router.refresh(), 30000);
    return () => window.clearInterval(t);
  }, [router]);
  React.useEffect(() => {
    if (!selected && batches.length > 0) {
      setSelected(batches[0].id);
    }
  }, [selected, batches]);

  const provisionalTotals = React.useMemo(() => {
    const gross = round2(provisional.reduce((s, r) => s + parseMoney(r.amount), 0));
    return { count: provisional.length, gross };
  }, [provisional]);

  const columns: ColumnDef<JournalRow>[] = React.useMemo(() => ([
    { header: "Datum", accessorKey: "date" },
    { header: "Beleg", accessorKey: "documentNo" },
    { header: "Ref", accessorKey: "ref" },
    { header: "Text", accessorKey: "text" },
    { header: "Soll", accessorKey: "debit" },
    { header: "Haben", accessorKey: "credit" },
    { header: "Betrag", accessorKey: "amount" },
    {
      header: "Status",
      accessorKey: "status",
      cell: (info) => renderStatus(info.row.original)
    },
    {
      header: "Beleg ansehen",
      id: "document_preview",
      cell: (info) => <DocumentPreviewButton entryId={info.row.original.id} documentNo={info.row.original.documentNo} />
    },
    {
      header: "Sperre",
      id: "row_lock",
      cell: (info) => (
        <LockCell
          row={info.row.original}
          reviewerId={reviewerId}
          reviewerName={reviewerName}
          onError={setError}
          onRefresh={() => router.refresh()}
        />
      )
    }
  ]), [reviewerId, reviewerName, router]);

  const selectedBatch = React.useMemo(() => {
    if (!selected) return null;
    return batches.find(b => b.id === selected) || null;
  }, [selected, batches]);

  async function createBatchSnapshot() {
    setError(null);
    setCreating(true);
    try {
      const res = await fetch("/api/batches/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || "Batch-Erstellung fehlgeschlagen");
      }
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch-Erstellung fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  }

  async function setBatchReviewed() {
    if (!selectedBatch) return;
    setActionBusy("review");
    setError(null);
    try {
      const res = await fetch("/api/batches/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batchId: selectedBatch.id })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || "Batch-Review fehlgeschlagen");
      }
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch-Review fehlgeschlagen");
    } finally {
      setActionBusy(null);
    }
  }

  async function setBatchFinal() {
    if (!selectedBatch) return;
    setActionBusy("finalize");
    setError(null);
    try {
      const res = await fetch("/api/batches/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batchId: selectedBatch.id })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || "Batch-Finalisierung fehlgeschlagen");
      }
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch-Finalisierung fehlgeschlagen");
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 style={{ marginBottom: 4 }}>Finance · Tagesbatch</h1>
          <div style={{ color: "var(--muted)", fontSize: 13 }}>
            Batch = Snapshot aller <strong>PROVISORISCH</strong> Buchungen (Review durch Team/Treuhand)
          </div>
          <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
            Legende: <span className="pill pill-review">≥ 90.0 % sicher</span>{" "}
            <span className="pill pill-provisional">&lt; 90.0 % PROVISORISCH</span>
          </div>
        </div>

        <div className="page-actions">
          <input
            className="input input-compact"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            placeholder="Bearbeiter Name"
            title="Name für Zeilen-Sperren (Mitarbeiter/Treuhand)"
          />
          <input
            className="input input-compact"
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            placeholder="Bearbeiter ID"
            title="Eindeutige ID für Zeilen-Sperren (Mitarbeiter/Treuhand)"
          />
          <button
            className="btn btn-primary"
            type="button"
            onClick={createBatchSnapshot}
            disabled={provisional.length === 0 || creating}
            title={provisional.length === 0 ? "Keine provisorischen Buchungen" : ""}
          >
            {creating ? "Erstellt..." : "Batch erstellen"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div className="table-wrap" style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Offen (provisorisch)</div>
          <div>Anzahl: <strong>{provisionalTotals.count}</strong></div>
          <div>Summe: <strong>CHF {provisionalTotals.gross.toFixed(2)}</strong></div>
          <div style={{ marginTop: 8, color: "var(--muted)", fontSize: 13 }}>
            Tipp: Erzeuge provisorische Buchungen über <strong>Rechnungen → Inbox → Auto-Buchen</strong>.
          </div>
        </div>

        <div className="table-wrap" style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Batches (Archiv)</div>
          {batches.length === 0 ? (
            <div style={{ color: "var(--muted)" }}>Noch keine Batches erstellt.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {batches.slice(0, 10).map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="btn"
                  onClick={() => setSelected(b.id)}
                  style={{
                    justifyContent: "space-between",
                    display: "flex",
                    gap: 10,
                    borderColor: selected === b.id ? "rgba(10,30,120,0.35)" : undefined,
                    background: selected === b.id ? "rgba(10,30,120,0.08)" : undefined
                  }}
                >
                  <span>
                    <strong>{b.batchId}</strong>{" "}
                    <span
                      className={
                        b.status === "FINAL"
                          ? "pill pill-final"
                          : b.status === "REVIEWED"
                            ? "pill pill-review"
                            : "pill pill-provisional"
                      }
                      style={{ marginLeft: 6 }}
                    >
                      {b.status}
                    </span>
                  </span>
                  <span style={{ color: "var(--muted)" }}>
                    {b.totals.count} · CHF {b.totals.gross.toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedBatch ? (
        <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>{selectedBatch.batchId}</div>
              <div style={{ color: "var(--muted)", fontSize: 13 }}>
                Datum: {selectedBatch.batchDate} · Einträge: {selectedBatch.totals.count} · Summe: CHF {selectedBatch.totals.gross.toFixed(2)}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn"
                type="button"
                onClick={setBatchReviewed}
                disabled={selectedBatch.status !== "OPEN" || actionBusy !== null}
              >
                {actionBusy === "review" ? "Review..." : "Als REVIEWED markieren"}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={setBatchFinal}
                disabled={selectedBatch.status === "FINAL" || actionBusy !== null}
              >
                {actionBusy === "finalize" ? "Finalisiert..." : "Batch finalisieren"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ marginBottom: 10, fontWeight: 800 }}>
        {selectedBatch ? "Batch-Inhalt" : "Offene provisorische Buchungen"}
      </div>

      <DataTable<JournalRow>
        columns={columns}
        data={selectedBatch ? selectedBatch.rows : provisional}
      />

      <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
        Mitarbeitende und Treuhand arbeiten direkt im System. Pro Zeile kann eine zeitlich begrenzte Sperre gesetzt werden, damit kein Parallelkonflikt entsteht.
      </div>
      {error && (
        <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
    </div>
  );
}
